/**
 * Platform detection and capability utilities for Tauri vs Web deployment
 */

export interface PlatformCapabilities {
  localDiscovery: boolean
  backgroundSync: boolean
  networkScan: boolean
  wifiScan: boolean  // Desktop-only: WiFi AP scanning for WLED setup
  secureStorage: boolean
  systemNotifications: boolean
}

export const Platform = {
  /**
   * Check if running in Tauri environment (desktop or mobile)
   */
  isTauri(): boolean {
    if (typeof window === 'undefined') return false
    
    const win = window as any
    return !!(
      win.__TAURI__ ||
      win.__TAURI_INTERNALS__ ||
      win.__TAURI_INVOKE__ ||
      (win.navigator?.userAgent?.includes('Tauri'))
    )
  },

  /**
   * Check if running on Android
   */
  isAndroid(): boolean {
    if (typeof window === 'undefined') return false
    return /android/i.test(navigator.userAgent)
  },

  /**
   * Check if running on iOS
   */
  isIOS(): boolean {
    if (typeof window === 'undefined') return false
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
  },

  /**
   * Check if running on a mobile device (Android or iOS)
   */
  isMobile(): boolean {
    return Platform.isAndroid() || Platform.isIOS()
  },

  /**
   * Check if running in Tauri desktop environment (not mobile)
   */
  isDesktop(): boolean {
    return Platform.isTauri() && !Platform.isMobile()
  },

  /**
   * Check if running in web browser (not Tauri)
   */
  isWeb(): boolean {
    return !Platform.isTauri()
  },

  /**
   * Get platform-specific capabilities
   */
  getCapabilities(): PlatformCapabilities {
    const isTauriDesktop = Platform.isDesktop()
    const isTauriMobile = Platform.isTauri() && Platform.isMobile()
    
    return {
      // Network discovery works on Tauri desktop and mobile
      localDiscovery: Platform.isTauri(),
      backgroundSync: Platform.isTauri(),
      // Network scanning (subnet scan) works on all Tauri platforms
      networkScan: Platform.isTauri(),
      // WiFi AP scanning only works on desktop (macOS/Windows/Linux)
      wifiScan: isTauriDesktop,
      secureStorage: true, // Both platforms support some form
      systemNotifications: isTauriDesktop
    }
  },

  /**
   * Check if a specific capability is available
   */
  hasCapability(capability: keyof PlatformCapabilities): boolean {
    const capabilities = Platform.getCapabilities()
    return capabilities[capability]
  },

  /**
   * Get platform name for logging/debugging
   */
  getPlatformName(): string {
    if (Platform.isAndroid()) return 'Tauri Android'
    if (Platform.isIOS()) return 'Tauri iOS'
    if (Platform.isDesktop()) return 'Tauri Desktop'
    return 'Web Browser'
  },

  /**
   * Get device identifier for credential storage tracking
   */
  async getDeviceId(): Promise<string> {
    if (Platform.isDesktop()) {
      // For desktop, create a consistent identifier
      const userAgent = navigator.userAgent
      const timestamp = Date.now().toString(36)
      return `desktop-${userAgent.slice(0, 10)}-${timestamp.slice(-6)}`
    } else {
      // For web, use browser info
      const browserInfo = navigator.userAgent.split(' ')[0] || 'web'
      const timestamp = Date.now().toString(36)
      return `${browserInfo.toLowerCase()}-${timestamp.slice(-6)}`
    }
  },

  /**
   * Check if running in development mode with Vite dev server
   */
  isDevelopment(): boolean {
    return import.meta.env.DEV
  },

  /**
   * Check if we should use the Vite proxy for Hue API calls
   * Use proxy in web development mode, direct HTTPS in Tauri
   */
  shouldUseHueProxy(): boolean {
    return Platform.isWeb() && Platform.isDevelopment()
  },

  /**
   * Build Hue API URL with optional dev proxy
   * @param ipAddress Bridge IP address
   * @param path API path (e.g., /clip/v2/resource/light)
   */
  buildHueApiUrl(ipAddress: string, path: string): string {
    if (Platform.shouldUseHueProxy()) {
      // Dev mode: Use Vite proxy
      return `/api/hue-bridge/${ipAddress}${path}`
    } else {
      // Production/Tauri: Direct HTTPS
      return `https://${ipAddress}${path}`
    }
  }
}

// Type guard for Tauri-specific APIs
export function isTauriWindow(window: Window): window is Window & { __TAURI__: Record<string, unknown> } {
  return Platform.isTauri()
}

// Runtime environment info
export const RuntimeEnvironment = {
  platform: Platform.getPlatformName(),
  capabilities: Platform.getCapabilities(),
  isTauri: Platform.isTauri(),
  isDesktop: Platform.isDesktop(),
  isMobile: Platform.isMobile(),
  isAndroid: Platform.isAndroid(),
  isIOS: Platform.isIOS(),
  isWeb: Platform.isWeb()
} as const